import React from 'react';
import { Card, CardHeader, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { CheckCircle2, XCircle, ChevronRight } from 'lucide-react';
import AIExplainPanel from '@/components/AIExplainPanel';

const BLOOM_LABELS = {
    remember: 'Hatırlama',
    understand: 'Anlama',
    apply: 'Uygulama',
    analyze: 'Analiz',
    evaluate: 'Değerlendirme',
    create: 'Oluşturma'
};

const QuizView = ({
    currentQuestion,
    currentQuestionIndex,
    questionsLength,
    selectedAnswer,
    showFeedback,
    onAnswerSelect,
    onSubmitAnswer,
    onNextQuestion
}) => {
    if (!currentQuestion) return null;

    const isOptionCorrect = (option, index) => {
        if (option && typeof option === 'object' && option.is_correct === true) return true;
        if (typeof currentQuestion.correct_answer === 'number') return currentQuestion.correct_answer === index;
        if (typeof currentQuestion.correct_answer === 'string') {
            const letter = String.fromCharCode(65 + index);
            return currentQuestion.correct_answer.toUpperCase() === letter;
        }
        return false;
    };

    return (
        <div className="space-y-6">
            <Card>
                <CardHeader>
                    <div className="flex items-center justify-between">
                        <Badge variant="outline">Soru {currentQuestionIndex + 1}</Badge>
                        <Badge variant="secondary">
                            {currentQuestion.bloom_level 
                                ? (BLOOM_LABELS[currentQuestion.bloom_level.toLowerCase()] || currentQuestion.bloom_level)
                                : 'Genel'}
                        </Badge>
                    </div>
                </CardHeader>
                <CardContent className="space-y-6">
                    <h3 className="text-lg font-medium leading-relaxed">
                        {currentQuestion.question_text}
                    </h3>
                    
                <div className="space-y-3">
                        {(currentQuestion.options || []).map((option, index) => {
                            const isSelected = selectedAnswer === index;
                            const isCorrect = isOptionCorrect(option, index);
                            const showCorrect = showFeedback && isCorrect;
                            const showWrong = showFeedback && isSelected && !isCorrect;
                            const optionText = typeof option === 'string' ? option : (option.text || option.text_tr || '');
                            const optionId = typeof option === 'string' ? String.fromCharCode(65 + index) : (option.id || String.fromCharCode(65 + index));
                            const showHintDot = isCorrect && !showFeedback;

                            return (
                                <button
                                    key={index}
                                    onClick={() => onAnswerSelect(index)}
                                    disabled={showFeedback}
                                    data-testid={`quiz-option-${optionId}`}
                                    className={`w-full p-4 rounded-lg border-2 text-left transition-all ${
                                        showCorrect
                                            ? 'border-green-500 bg-green-500/10'
                                            : showWrong
                                                ? 'border-red-500 bg-red-500/10'
                                                : isSelected
                                                    ? 'border-primary bg-primary/10'
                                                    : 'border-border hover:border-primary/50'
                                    }`}
                                >
                                    <div className="flex items-center gap-3">
                                        <span className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-medium ${
                                            showCorrect
                                                ? 'bg-green-500 text-white'
                                                : showWrong
                                                    ? 'bg-red-500 text-white'
                                                    : isSelected
                                                        ? 'bg-primary text-primary-foreground'
                                                        : 'bg-secondary'
                                        }`}>
                                            {optionId}
                                        </span>
                                        <span className="flex-1">
                                            {optionText}
                                            {showHintDot && <span className="quiz-hint-dot">·</span>}
                                        </span>
                                        {showCorrect && <CheckCircle2 className="w-5 h-5 text-green-500" />}
                                        {showWrong && <XCircle className="w-5 h-5 text-red-500" />}
                                    </div>
                                </button>
                            );
                        })}
                    </div>
                    
                    {!showFeedback ? (
                        <Button 
                            onClick={onSubmitAnswer} 
                            className="w-full"
                            disabled={selectedAnswer === null}
                        >
                            Cevabi Kontrol Et
                        </Button>
                    ) : (
                        <div className="space-y-3">
                            {/* Explanation + Wrong Answer Guidance */}
                            <div className={`p-3 rounded-xl text-sm ${
                                isOptionCorrect(currentQuestion.options?.[selectedAnswer], selectedAnswer) 
                                    ? 'bg-emerald-500/10 border border-emerald-500/15' 
                                    : 'bg-red-500/10 border border-red-500/15'
                            }`}>
                                {/* Wrong answer specific guidance */}
                                {!isOptionCorrect(currentQuestion.options?.[selectedAnswer], selectedAnswer) && currentQuestion.options?.[selectedAnswer]?.wrong_answer_guidance && (
                                    <p className="text-xs text-red-400/80 mb-2">
                                        <strong>Bu sik neden yanlis:</strong> {currentQuestion.options[selectedAnswer].wrong_answer_guidance}
                                    </p>
                                )}
                                {/* General explanation */}
                                {currentQuestion.explanation && (
                                    <p className="text-xs text-foreground/60">{currentQuestion.explanation}</p>
                                )}
                                {/* Regulation reference */}
                                {currentQuestion.regulation_reference && (
                                    <div className="flex flex-wrap gap-1 mt-2 pt-2 border-t border-border/30">
                                        {typeof currentQuestion.regulation_reference === 'string' ? (
                                            <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded text-[9px] bg-primary/8 border border-primary/10 text-primary/70 cursor-pointer hover:bg-primary/15">
                                                {currentQuestion.regulation_reference}
                                            </span>
                                        ) : currentQuestion.regulation_reference.description ? (
                                            <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded text-[9px] bg-primary/8 border border-primary/10 text-primary/70 cursor-pointer hover:bg-primary/15">
                                                {currentQuestion.regulation_reference.description}
                                            </span>
                                        ) : null}
                                    </div>
                                )}
                            </div>
                            {/* AI ile detaylı açıklama (RAG + Gemini) */}
                            <AIExplainPanel
                                questionId={currentQuestion.id}
                                selectedAnswer={selectedAnswer}
                            />
                            <Button onClick={onNextQuestion} className="w-full">
                                {currentQuestionIndex === questionsLength - 1 ? 'Sonuclari Gor' : 'Sonraki Soru'}
                                <ChevronRight className="w-4 h-4 ml-2" />
                            </Button>
                        </div>
                    )}
                </CardContent>
            </Card>
        </div>
    );
};

export default QuizView;
